import { Fields as TargetSegment } from 'models/TargetSegment'
import { Customer } from 'types/customer'
import { api } from './index'

type CheckSegmentNameParams = {
  name: string
}

type CheckSegmentNameResponse = {
  available: boolean
}

type GetSegmentParams = {
  segmentId: string
}

type GetSegmentResponse = {
  target_segment: TargetSegment
}

type GetSampleCustomersParams = {
  segmentId: string
  page?: number
  per_page?: number
  cancelToken?: any
}

type GetSampleCustomersResponse = {
  sample_customers: Customer[]
  customer_count: number
  pagination: {
    total_pages: number
    per_page: number
    current_page: number
    total_count: number
  }
}

type GetTargetSegmentsResponse = {
  target_segments: TargetSegment[]
}

type RawSegmentActivationPreferenceResponse = {
  auto_approve_ai_segments: boolean | null
}

export type SegmentActivationPreference = {
  autoApproveAiSegments: boolean | null
}

const transformSegmentActivationPreference = (
  raw: RawSegmentActivationPreferenceResponse
): SegmentActivationPreference => ({
  autoApproveAiSegments: raw.auto_approve_ai_segments,
})

export const segmentsApi = api.injectEndpoints({
  endpoints: builder => ({
    getTargetSegments: builder.query<GetTargetSegmentsResponse, void>({
      query: () => ({
        url: 'target_segments',
        method: 'GET',
      }),
      providesTags: ['TargetSegment'],
    }),

    checkSegmentName: builder.query<
      CheckSegmentNameResponse,
      CheckSegmentNameParams
    >({
      query: ({ name }) => ({
        url: `target_segments/check-name?name=${encodeURIComponent(name)}`,
        method: 'GET',
      }),
    }),

    getSegment: builder.query<GetSegmentResponse, GetSegmentParams>({
      query: ({ segmentId }) => ({
        url: `target_segments/${segmentId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { segmentId }) => [
        { type: 'TargetSegment', id: segmentId },
      ],
    }),

    getSampleCustomers: builder.query<
      GetSampleCustomersResponse,
      GetSampleCustomersParams
    >({
      query: ({ segmentId, page = 1, per_page = 10, cancelToken }) => ({
        url: `target_segments/${segmentId}/sample_customers`,
        method: 'GET',
        params: { page, per_page },
        cancelToken,
      }),
      providesTags: (result, error, { segmentId }) => [
        { type: 'SegmentSampleCustomers', id: segmentId },
      ],
    }),

    getSegmentActivationPreference: builder.query<
      SegmentActivationPreference,
      void
    >({
      query: () => ({
        url: 'segment_activation_preferences',
        method: 'GET',
      }),
      transformResponse: transformSegmentActivationPreference,
      providesTags: ['SegmentActivationPreference'],
    }),

    updateSegmentActivationPreference: builder.mutation<
      SegmentActivationPreference,
      SegmentActivationPreference
    >({
      query: body => ({
        url: 'segment_activation_preferences',
        method: 'PATCH',
        body: { auto_approve_ai_segments: body.autoApproveAiSegments },
      }),
      transformResponse: transformSegmentActivationPreference,
      invalidatesTags: ['SegmentActivationPreference'],
    }),
  }),
})

export const {
  useGetTargetSegmentsQuery,
  useCheckSegmentNameQuery,
  useLazyCheckSegmentNameQuery,
  useGetSegmentQuery,
  useGetSampleCustomersQuery,
  useGetSegmentActivationPreferenceQuery,
  useUpdateSegmentActivationPreferenceMutation,
} = segmentsApi

// Action compatible with infinite scroll hook
export const getSampleCustomersAction = (params: GetSampleCustomersParams) =>
  segmentsApi.endpoints.getSampleCustomers.initiate(params)
